import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter } from "lucide-react";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";

const CATEGORIES = ["All", "Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "General"];

export default function CoursesPage() {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [sort, setSort] = useState("latest");
  const [contentType, setContentType] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, sort, contentType]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "All") params.append("category", category);
      if (sort) params.append("sort", sort);
      if (contentType) params.append("content_type", contentType);
      const { data } = await api.get(`/courses?${params.toString()}`);
      setCourses(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-with-nav bg-white min-h-screen" data-testid="courses-page">
      {/* Filters */}
      <div className="bg-slate-50 px-4 py-3 space-y-3">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-700">Filters</span>
        </div>
        {/* Category pills */}
        <div className="flex overflow-x-auto gap-2 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`filter-cat-${cat.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 text-xs font-bold px-3 py-2 transition-none ${
                category === cat
                  ? "bg-[#0284C7] text-white"
                  : "bg-white text-slate-600 border border-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        {/* Sort & Type */}
        <div className="flex gap-2">
          <select
            data-testid="sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="flex-1 bg-white border border-slate-200 text-xs px-3 py-2 focus:outline-none"
          >
            <option value="latest">Latest</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
          </select>
          <select
            data-testid="type-select"
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            className="flex-1 bg-white border border-slate-200 text-xs px-3 py-2 focus:outline-none"
          >
            <option value="">All Types</option>
            <option value="course">Courses</option>
            <option value="book">Books</option>
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="px-4 py-4">
        <p className="text-xs text-slate-500 mb-3">{courses.length} results found</p>
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="courses-grid">
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
        {!loading && courses.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-sm">
            No courses found for this filter.
          </div>
        )}
      </div>
    </div>
  );
}
